import { DestroyRef, signal } from '@angular/core';

/** Intervalo padrão da atualização automática das telas do Gerencial. */
export const INTERVALO_AUTO_ATUALIZACAO_MS = 30_000;

/** Elementos que indicam modal/diálogo aberto — com algum visível, a tela não recarrega. */
const SELETORES_MODAL = [
  '.modal-backdrop', // app-modal (shared/components/modal)
  '.modal-overlay',
  '.column-modal-overlay',
  '.modal-container',
  '.modal-box',
  '.reiniciar-modal',
  '.pdf-preview-modal',
  '.modal.show',
  '[role="dialog"]',
  '.drawer-overlay',
  '.cdk-overlay-pane',
];

export interface OpcoesAutoAtualizacao {
  /** Intervalo entre recargas (padrão 30 s). */
  intervaloMs?: number;
  /** Retorne true para pular o ciclo (ex.: edição em andamento ou carga anterior ainda rodando). */
  pausar?: () => boolean;
}

/** Estado da atualização automática da tela aberta — exibido no indicador do rodapé. */
export interface EstadoAutoAtualizacao {
  ativa: boolean;
  ultima: Date | null;
  proxima: Date | null;
  intervaloMs: number;
  /** Motivo do último ciclo pulado (null = rodou normalmente). */
  pausaMotivo: string | null;
}

const ESTADO_INICIAL: EstadoAutoAtualizacao = { ativa: false, ultima: null, proxima: null, intervaloMs: 0, pausaMotivo: null };

export const estadoAutoAtualizacao = signal<EstadoAutoAtualizacao>(ESTADO_INICIAL);

// Só uma tela (componente de rota) fica registrada por vez; o id evita que a tela anterior,
// ao ser destruída depois da nova, apague o estado da atual.
let registroAtual: { id: number; executar: () => void } | null = null;
let proximoId = 0;

function visivel(el: Element): boolean {
  const e = el as HTMLElement;
  if (e.getClientRects().length === 0) return false;
  const estilo = getComputedStyle(e);
  return estilo.display !== 'none' && estilo.visibility !== 'hidden';
}

export function haModalAberto(doc: Document = document): boolean {
  return SELETORES_MODAL.some(seletor => Array.from(doc.querySelectorAll(seletor)).some(visivel));
}

/** Motivo para não recarregar agora (null = pode recarregar). */
export function motivoPausa(doc: Document, pausar?: () => boolean): string | null {
  if (doc.hidden) return 'aba em segundo plano';
  if (haModalAberto(doc)) return 'janela aberta na tela';
  if (pausar?.()) return 'carregando ou editando';
  return null;
}

/** Decide se o ciclo atual deve recarregar a tela. */
export function deveAtualizar(doc: Document, pausar?: () => boolean): boolean {
  return motivoPausa(doc, pausar) === null;
}

/** Recarrega a tela registrada agora (botão "Atualizar agora" do indicador). */
export function atualizarAgora(): void {
  registroAtual?.executar();
}

/**
 * Recarrega a tela periodicamente para refletir alterações de outros usuários e dos robôs.
 * Não recarrega com a aba em segundo plano nem com modal aberto; ao voltar para a aba, recarrega
 * na hora se o intervalo já passou. O timer é encerrado junto com o componente.
 */
export function iniciarAutoAtualizacao(
  destroyRef: DestroyRef,
  recarregar: () => void,
  opcoes: OpcoesAutoAtualizacao = {},
): void {
  const intervalo = opcoes.intervaloMs ?? INTERVALO_AUTO_ATUALIZACAO_MS;
  const meuId = ++proximoId;
  let ultimaRecarga = Date.now();

  const publicar = (pausaMotivo: string | null) => {
    if (registroAtual?.id !== meuId) return;
    estadoAutoAtualizacao.set({
      ativa: true,
      ultima: new Date(ultimaRecarga),
      proxima: new Date(Date.now() + intervalo),
      intervaloMs: intervalo,
      pausaMotivo,
    });
  };

  const executar = () => {
    ultimaRecarga = Date.now();
    recarregar();
    publicar(null);
  };

  const tentar = () => {
    const motivo = motivoPausa(document, opcoes.pausar);
    if (motivo) {
      publicar(motivo);
      return;
    }
    executar();
  };

  registroAtual = {
    id: meuId,
    executar: () => {
      // Manual: só respeita a pausa da própria tela (carga em andamento)
      if (!opcoes.pausar?.()) executar();
    },
  };
  publicar(null);

  const id = setInterval(tentar, intervalo);
  const aoMudarVisibilidade = () => {
    if (!document.hidden && Date.now() - ultimaRecarga >= intervalo) tentar();
  };
  document.addEventListener('visibilitychange', aoMudarVisibilidade);

  destroyRef.onDestroy(() => {
    clearInterval(id);
    document.removeEventListener('visibilitychange', aoMudarVisibilidade);
    if (registroAtual?.id === meuId) {
      registroAtual = null;
      estadoAutoAtualizacao.set(ESTADO_INICIAL);
    }
  });
}
